/**
 * OWL2 Annotation Types
 *
 * Defines annotation structures used for metadata in OWL2 ontologies.
 * Annotations can be attached to axioms, entities, and other annotations.
 */

import type { IRI } from './IRI.ts';
import type { Literal } from './Literal.ts';
import type { AnonymousIndividual } from './Entity.ts';

// =============================================================================
// Annotation Property
// =============================================================================

/**
 * Annotation property reference.
 * Example: rdfs:label, rdfs:comment, owl:deprecated
 */
export interface AnnotationProperty {
    readonly type: 'AnnotationProperty';
    readonly iri: IRI;
}

// =============================================================================
// Annotation Value
// =============================================================================

/**
 * Possible values for an annotation.
 * Can be an IRI, an anonymous individual, or a literal.
 */
export type AnnotationValue = IRI | AnonymousIndividual | Literal;

// =============================================================================
// Annotation Subject
// =============================================================================

/**
 * Possible subjects for an annotation assertion.
 * Can be an IRI or an anonymous individual.
 */
export type AnnotationSubject = IRI | AnonymousIndividual;

// =============================================================================
// Annotation
// =============================================================================

/**
 * An annotation - property/value pair with optional nested annotations.
 * OWL2: Annotation(AP AV) where AP is an annotation property and AV is an annotation value.
 */
export interface Annotation {
    /** The annotation property */
    readonly property: AnnotationProperty;
    /** The annotation value */
    readonly value: AnnotationValue;
    /** Optional nested annotations on this annotation */
    readonly annotations?: readonly Annotation[];
}

// =============================================================================
// Factory Functions
// =============================================================================

/**
 * Create an annotation property reference.
 */
export function annotationProperty(iri: IRI): AnnotationProperty {
    return { type: 'AnnotationProperty', iri };
}

/**
 * Create an annotation.
 */
export function annotation(
    property: AnnotationProperty,
    value: AnnotationValue,
    annotations?: readonly Annotation[]
): Annotation {
    return {
        property,
        value,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an annotation with a literal value.
 */
export function annotationWithLiteral(
    property: AnnotationProperty,
    literal: Literal,
    annotations?: readonly Annotation[]
): Annotation {
    return annotation(property, literal, annotations);
}

/**
 * Create an annotation with an IRI value.
 */
export function annotationWithIRI(
    property: AnnotationProperty,
    iri: IRI,
    annotations?: readonly Annotation[]
): Annotation {
    return annotation(property, iri, annotations);
}

// =============================================================================
// Type Guards for Annotation Values
// =============================================================================

/**
 * Check if an annotation value is an IRI.
 */
export function isAnnotationValueIRI(value: AnnotationValue): value is IRI {
    return 'kind' in value && (value.kind === 'FullIRI' || value.kind === 'AbbreviatedIRI');
}

/**
 * Check if an annotation value is an anonymous individual.
 */
export function isAnnotationValueAnonymous(value: AnnotationValue): value is AnonymousIndividual {
    return 'type' in value && value.type === 'AnonymousIndividual';
}

/**
 * Check if an annotation value is a literal.
 */
export function isAnnotationValueLiteral(value: AnnotationValue): value is Literal {
    return 'kind' in value && (value.kind === 'TypedLiteral' || value.kind === 'PlainLiteral');
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get all annotation property IRIs from a list of annotations (recursive).
 */
export function getAnnotationPropertyIRIs(annotations: readonly Annotation[]): IRI[] {
    const iris: IRI[] = [];
    for (const ann of annotations) {
        iris.push(ann.property.iri);
        if (ann.annotations) {
            iris.push(...getAnnotationPropertyIRIs(ann.annotations));
        }
    }
    return iris;
}

/**
 * Find annotations with a specific property IRI.
 */
export function findAnnotationsByProperty(
    annotations: readonly Annotation[],
    propertyIRI: IRI
): readonly Annotation[] {
    return annotations.filter(ann => {
        if (ann.property.iri.kind !== propertyIRI.kind) return false;
        if (ann.property.iri.kind === 'FullIRI' && propertyIRI.kind === 'FullIRI') {
            return ann.property.iri.value === propertyIRI.value;
        }
        if (ann.property.iri.kind === 'AbbreviatedIRI' && propertyIRI.kind === 'AbbreviatedIRI') {
            return ann.property.iri.prefix === propertyIRI.prefix &&
                   ann.property.iri.localName === propertyIRI.localName;
        }
        return false;
    });
}
